import CertificationProject from "@/models/CertificationProject";
import { setDisplayedItems } from "@/utils/setDisplayedItems";
import { list, retrieve } from "@/utils/factoryRest";
import { defineStore } from "pinia";
import { useRepo } from "pinia-orm";
import { formatUser, useUserStore } from "./user.js";
import CertipersUser from "@/models/User.js";

import { CertificationProjectApi } from "@socotec.io/certipers-back-sdk";
import configs from "@/setup/openApiConfig";

const certificationProjectsApi = new CertificationProjectApi(configs.certipersBackConfig);

const certificationProjectsRepo = useRepo(CertificationProject);
const certipersUserRepo = useRepo(CertipersUser);

export const formatCertificationProject = (certificationProject) => {
  const extensionOfNested = typeof certificationProject.extensionOf === "object";
  return {
    ...certificationProject,
    certipersUserUuid: certificationProject.user,
    cycleUuid: certificationProject.cycle,
    extensionOfUuid: extensionOfNested
      ? certificationProject.extensionOf.uuid
      : certificationProject.extensionOf,
    ...(extensionOfNested && {
      extensionOfData: formatCertificationProject(certificationProject.extensionOf),
    }),
  };
};

export const formatCertificationProjectWithExaminations = (certificationProject) => {
  const formatted = formatCertificationProject(certificationProject);
  formatted.certipersUserUuid = certificationProject.user.uuid;
  return formatted;
};

export const useCertificationProjectStore = defineStore("certificationProject", {
  state: () => ({
    itemsCount: 0,
    uncertifiedCertificationProjectCount: 0,
    certifiedCertificationProjectCount: 0,
  }),

  actions: {
    fetchCertificationProjectsWithExaminations: list({
      ModelRepo: certificationProjectsRepo,
      restListMethod:
        certificationProjectsApi.listCertificationProjectsWithExaminationDecisions.bind(
          certificationProjectsApi,
        ),
      insertMode: "save",
      handleResponseData: ({ results }) => {
        const collected_users = results.map(({ user }) => formatUser(user));
        certipersUserRepo.save(collected_users);
        useUserStore().setItemsCount(collected_users.length);
        return results.map(formatCertificationProjectWithExaminations);
      },
      onItemsInserted: (responseData) => {
        setDisplayedItems(certificationProjectsRepo, responseData.results);
        useCertificationProjectStore().setItemsCount(responseData.count);
      },
    }),
    fetchCertificationProjectsToRenew: list({
      ModelRepo: certificationProjectsRepo,
      restListMethod: certificationProjectsApi.listRenewals.bind(certificationProjectsApi),
      insertMode: "save",
      handleResponseData: ({ results }) => {
        certipersUserRepo.save(results.map(({ user }) => formatUser(user)));
        return results.map(formatCertificationProjectWithExaminations);
      },
      onItemsInserted: (responseData) => {
        setDisplayedItems(certificationProjectsRepo, responseData.results);
        useCertificationProjectStore().setItemsCount(responseData.count);
      },
    }),
    fetchCertificationProjects: list({
      ModelRepo: certificationProjectsRepo,
      restListMethod: certificationProjectsApi.list.bind(certificationProjectsApi),
      insertMode: "save",
      handleResponseData: ({ results }) => {
        return results.map(formatCertificationProject);
      },
      onItemsInserted: (responseData) => {
        setDisplayedItems(certificationProjectsRepo, responseData.results);
        useCertificationProjectStore().setItemsCount(responseData.count);
      },
    }),
    fetchMyCertificationProjects: list({
      ModelRepo: certificationProjectsRepo,
      insertMode: "save",
      restListMethod: certificationProjectsApi.listByUser.bind(certificationProjectsApi),
      handleResponseData: ({ results }) => {
        return results.map(formatCertificationProject);
      },
      onItemsInserted: (responseData) => {
        setDisplayedItems(certificationProjectsRepo, responseData.results);
        useCertificationProjectStore().setItemsCount(responseData.count);
      },
    }),

    retrieveCertificationProject: retrieve({
      ModelRepo: certificationProjectsRepo,
      restRetrieveMethod: certificationProjectsApi.retrieve.bind(certificationProjectsApi),
      handleResponseData: (response) => {
        return formatCertificationProject(response);
      },
    }),

    async fetchCertificationProjectCount({ filters = {} } = {}) {
      const response = await certificationProjectsApi.countByUserByStatus(filters);
      return response.count;
    },

    async createCertificationProject(payloadCycleUser) {
      const response = await certificationProjectsApi.create({
        certificationProjectRequest: payloadCycleUser,
      });
      return certificationProjectsRepo.save(formatCertificationProject(response));
    },

    async createCertificationProjectAndApplyTransferIn(payloadCycleUser) {
      const response = await certificationProjectsApi.createAndApplyTransferIn({
        certificationProjectTransferRequest: payloadCycleUser,
      });
      return certificationProjectsRepo.save(formatCertificationProject(response));
    },

    async partialUpdateCertificationProject({ certificationProject }) {
      const { uuid, ...body } = certificationProject;
      const DATE_FIELDS = [
        "certifiedDate",
        "manualCertifiedDate",
        "sanctionDate",
        "oldCertifiedDate",
        "transferDate",
        "effectiveTransferDate",
        "dueDate",
        "mentionExtensionDate",
        "mentionReductionDate",
      ];
      DATE_FIELDS.forEach((field) => {
        if (field in body) body[field] = body[field] ? new Date(body[field]) : null;
      });
      const response = await certificationProjectsApi.partialUpdate({
        uuid,
        patchedCertificationProjectRequest: body,
      });
      return certificationProjectsRepo.save(formatCertificationProject(response));
    },

    async updateCertificationProjectCertifiedDate({ uuid, certifiedDate, manualCertifiedDate }) {
      const response = await certificationProjectsApi.updateCertifiedDate({
        uuid,
        patchedUpdateCertifiedDateRequestRequest: {
          certifiedDate,
          manualCertifiedDate,
        },
      });
      return certificationProjectsRepo.save(formatCertificationProject(response));
    },

    async mentionReduction(uuid) {
      const response = await certificationProjectsApi.mentionReduction({
        uuid,
      });
      return certificationProjectsRepo.save(response.results.map(formatCertificationProject));
    },

    async mentionExtension(uuid) {
      const response = await certificationProjectsApi.mentionExtension({
        uuid,
      });
      return certificationProjectsRepo.save(response.results.map(formatCertificationProject));
    },

    async stopWithExtensionManagement(uuid) {
      const response = await certificationProjectsApi.stopWithMentionManagement({ uuid });
      return certificationProjectsRepo.save(formatCertificationProject(response));
    },

    setItemsCount(count) {
      this.itemsCount = count;
    },

    setCertifiedCertificationProject(count) {
      this.certifiedCertificationProjectCount = count;
    },

    setUncertifiedCertificationProject(count) {
      this.uncertifiedCertificationProjectCount = count;
    },
  },

  getters: {
    getCertificationsProjects: () => () => {
      return certificationProjectsRepo
        .with("cycleData", (query) =>
          query.with("domainVersionData", (query) => query.with("domainData")),
        )
        .get();
    },
    getCertificationProjectCount: (state) => {
      return state.itemsCount;
    },
    getCertificationsProjectsDisplayed: () => () => {
      return certificationProjectsRepo
        .with("cycleData", (query) =>
          query.with("domainVersionData", (query) => query.with("domainData")),
        )
        .with("certipersUserData")
        .where("isDisplayed", true)
        .get();
    },
    getUncertifiedCertificationProjectCount: (state) => {
      return state.uncertifiedCertificationProjectCount;
    },
    getCertifiedCertificationProjectCount: (state) => {
      return state.certifiedCertificationProjectCount;
    },
  },
});
